import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import { doc, getDoc, setDoc, collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";

export default function DashboardManager() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parties, setParties] = useState([]);
  const [userRole, setUserRole] = useState(null);

  const [data, setData] = useState({
    schemes: "",
    earnings: "",
    winnerId: "",
    winnerName: "",
    winnerRank: "Rank 1",
    schemeTitle: "Schemes Jan'26"
  });

  useEffect(() => {
    const fetchData = async () => {
      // 1. Check Auth & Role
      const user = auth.currentUser;
      if (!user) {
        navigate("/");
        return;
      }

      const userSnap = await getDoc(doc(db, "users", user.uid));
      if (userSnap.exists()) {
        const role = userSnap.data().role?.toLowerCase()?.trim();
        setUserRole(role);
        if (!["admin", "superadmin", "operator"].includes(role)) {
          navigate("/base");
          return;
        }
      }

      // 2. Fetch parties (Hawkers only)
      const q = query(collection(db, "partyMaster"), where("partytype", "==", "hawker"));
      const partySnap = await getDocs(q);
      setParties(partySnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // 3. Fetch current dashboard data
      const configSnap = await getDoc(doc(db, "settings", "hawkerCommunications"));
      if (configSnap.exists()) {
        const d = configSnap.data();
        setData({
          ...d,
          schemes: (d.schemes || []).join("\n"),
          earnings: (d.earnings || []).join("\n")
        });
      }
      setLoading(false);
    };

    fetchData();
  }, [navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...data,
        schemes: data.schemes.split("\n").filter(s => s.trim() !== ""),
        earnings: data.earnings.split("\n").filter(e => e.trim() !== ""),
        updatedAt: new Date()
      };
      await setDoc(doc(db, "settings", "hawkerCommunications"), payload);
      alert("Dashboard updated successfully!");
    } catch (err) {
      alert("Error saving: " + err.message);
    }
    setSaving(false);
  };

  const handleWinnerSelect = (id) => {
    const p = parties.find(x => x.id === id);
    if (p) {
      setData(prev => ({ ...prev, winnerId: id, winnerName: p.name }));
    }
  };

  if (loading) return <div style={{ padding: 20 }}>Loading Manager...</div>;

  return (
    <Layout>
      <div style={{ padding: "40px 20px", background: "#f8fafc", minHeight: "100vh", fontFamily: "'Inter', sans-serif" }}>
        <div style={{ maxWidth: "700px", margin: "0 auto" }}>
          
          {/* HEADER */}
          <div style={{ marginBottom: 32, borderLeft: "6px solid #6366f1", paddingLeft: 20 }}>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: "#1e293b", letterSpacing: "-0.025em" }}>
              Hawker Communications
            </h1>
            <p style={{ marginTop: 4, fontSize: 14, color: "#64748b", fontWeight: 500 }}>
              Update schemes, earnings, and recognition for the hawker app
            </p>
          </div>

          <div style={{ background: "#ffffff", borderRadius: 16, padding: "32px", boxShadow: "0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -2px rgba(0,0,0,0.05)", border: "1px solid #e2e8f0" }}>
            
            {/* CONTENT SECTION */}
            <h3 style={sectionTitleStyle}>📢 DASHBOARD CONTENT</h3>
            
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>SCHEME TITLE</label>
              <input 
                type="text" 
                value={data.schemeTitle} 
                onChange={e => setData({...data, schemeTitle: e.target.value})}
                style={inputStyle}
                placeholder="e.g. Schemes Jan'26"
              />
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>CURRENT SCHEMES (ONE PER LINE)</label>
              <textarea 
                rows="4" 
                value={data.schemes} 
                onChange={e => setData({...data, schemes: e.target.value})}
                style={textareaStyle}
                placeholder="Enter schemes clearly..."
              />
            </div>

            <div style={{ marginBottom: 32 }}>
              <label style={labelStyle}>UPCOMING EARNINGS (ONE PER LINE)</label>
              <textarea 
                rows="4" 
                value={data.earnings} 
                onChange={e => setData({...data, earnings: e.target.value})}
                style={textareaStyle}
                placeholder="Enter earning opportunities..."
              />
            </div>

            <div style={{ height: "1px", background: "#f1f5f9", margin: "32px 0" }}></div>

            {/* WINNER SECTION */}
            <h3 style={sectionTitleStyle}>🏆 WINNER RECOGNITION</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: 32 }}>
              <div>
                <label style={labelStyle}>SELECT WINNER</label>
                <select 
                  value={data.winnerId} 
                  onChange={e => handleWinnerSelect(e.target.value)}
                  style={inputStyle}
                >
                  <option value="">-- Choose Party --</option>
                  {parties.map(p => (
                    <option key={p.id} value={p.id}>{p.name} ({p.partyId})</option>
                  ))}
                </select>
              </div>
              <div>
                <label style={labelStyle}>WINNER RANK TEXT</label>
                <input 
                  type="text" 
                  value={data.winnerRank} 
                  onChange={e => setData({...data, winnerRank: e.target.value})}
                  style={inputStyle}
                  placeholder="e.g. Rank 1"
                />
              </div>
            </div>

            <button 
              onClick={handleSave} 
              disabled={saving}
              style={saveButtonStyle}
            >
              {saving ? "SAVING UPDATES..." : "🚀 PUSH TO DASHBOARD"}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

const sectionTitleStyle = {
  fontSize: "12px",
  fontWeight: 800,
  color: "#94a3b8",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  marginBottom: "20px",
};

const labelStyle = {
  display: "block",
  fontSize: "11px",
  fontWeight: 700,
  color: "#64748b",
  marginBottom: "8px",
  letterSpacing: "0.02em"
};

const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  fontSize: "15px",
  borderRadius: "10px",
  border: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#1e293b",
  outline: "none",
  transition: "border-color 0.2s",
  boxSizing: "border-box"
};

const textareaStyle = {
  ...inputStyle,
  resize: "vertical",
  minHeight: "100px",
  lineHeight: "1.5"
};

const saveButtonStyle = {
  width: "100%",
  padding: "16px",
  background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
  color: "white",
  border: "none",
  borderRadius: "12px",
  fontSize: "16px",
  fontWeight: 700,
  cursor: "pointer",
  boxShadow: "0 4px 6px -1px rgba(79, 70, 229, 0.3)",
  transition: "transform 0.1s ease",
};
